import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, ChevronRight, CalendarCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import BookingEnquiryDialog from '../booking/BookingEnquiryDialog';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (href) => {
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    {
      label: 'Services',
      href: '#ecosystem',
      dropdown: [
        { label: 'Dog Boarding', path: '/petcart-nest' },
        { label: 'Dog Walking', external: 'https://freshtails.zopnote.com/login' },
        { label: 'Day Care', path: '/services/daycare' },
        { label: 'Recreation', path: '/services/recreation' },
        { label: 'Grooming', path: '/services/grooming' }
      ]
    },
    {
      label: 'Facilities',
      href: '#facilities',
      dropdown: [
        {
          label: 'Resort & Booking',
          dropdown: [
            { label: 'PetCart Nest', path: '/petcart-nest' }
          ]
        }
      ]
    },
    {
      label: 'Freshtails',
      path: '/freshtails',
      dropdown: [
        { label: 'Dog Walking', external: 'https://freshtails.zopnote.com/login' },
        { label: 'Dog Boarding', external: 'https://freshtails.zopnote.com/login' },
        { label: 'Dog Swimming', external: 'https://freshtails.zopnote.com/login' }
      ]
    },
    { label: 'Wag and Wine', path: '/wag-and-wine' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' }
  ];

  const closeAll = () => {
    setActiveDropdown(null);
    setActiveSubmenu(null);
  };

  const handleNavClick = (item, e) => {
    if (item.path) {
      e.preventDefault();
      navigate(item.path);
      return;
    }
    if (item.href) {
      e.preventDefault();
      goToSection(item.href);
    }
  };

  const handleContactNow = (e) => {
    e.preventDefault();
    const tel = 'tel:+918861712345';
    const wa = 'https://wa.me/918861712345';
    const start = Date.now();
    const fallback = () => {
      if (Date.now() - start < 2000 && !document.hidden) {
        window.open(wa, '_blank', 'noopener,noreferrer');
      }
    };
    const timer = setTimeout(fallback, 1500);
    const onVisibility = () => {
      if (document.hidden) {
        clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.location.href = tel;
  };

  const handleDropdownClick = (dropItem) => {
    if (dropItem.dropdown) return;
    if (dropItem.external) {
      window.open(dropItem.external, '_blank', 'noopener,noreferrer');
      return;
    }
    if (dropItem.path) {
      navigate(dropItem.path);
    } else if (dropItem.href) {
      goToSection(dropItem.href);
    }
    closeAll();
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${isScrolled ? 'shadow-sm' : ''}`}>
      <div className="relative mx-auto flex h-16 max-w-[1480px] items-center justify-start px-4 sm:px-6 lg:justify-between lg:gap-4 lg:px-8">
        <div className="flex min-w-0 flex-1 items-center justify-start gap-6 lg:gap-8">
          <a href="/" className="flex shrink-0 items-center">
            <img
              src="/images/logo-petcart-color.png"
              alt="PetCart Logo"
              className="h-8 w-auto sm:h-9 lg:h-10"
            />
          </a>

          {/* Desktop Navigation - Hover-based dropdowns */}
          <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.dropdown && setActiveDropdown(item.label)}
              onMouseLeave={() => { setActiveDropdown(null); setActiveSubmenu(null); }}
            >
              <a
                href={item.href || item.path || '#'}
                onClick={(e) => handleNavClick(item, e)}
                className="landing-nav-link flex cursor-pointer items-center py-2 transition-colors hover:text-primary"
              >
                {item.label}
                {item.dropdown && <ChevronDown className="ml-1" size={16} />}
              </a>

              {/* Dropdown Menu - Hover based */}
              {item.dropdown && activeDropdown === item.label && (
                <div className="absolute top-full left-0 pt-2">
                  <div className="min-w-[220px] rounded-lg border border-neutral-200 bg-white py-2 shadow-lg">
                    {item.dropdown.map((dropItem) => (
                      <div
                        key={dropItem.label}
                        className="relative"
                        onMouseEnter={() => setActiveSubmenu(dropItem.dropdown ? dropItem.label : null)}
                      >
                        <button
                          onClick={() => handleDropdownClick(dropItem)}
                          className="landing-nav-dropdown-item w-full"
                        >
                          <span>{dropItem.label}</span>
                          {dropItem.suffix && (
                            <span className="text-xs font-normal text-neutral-400 opacity-60">({dropItem.suffix})</span>
                          )}
                          {dropItem.isNew && (
                            <span className="ml-auto rounded-full bg-gradient-to-r from-primary to-primary-dark px-2 py-0.5 text-xs font-semibold text-white">
                              New
                            </span>
                          )}
                          {dropItem.dropdown && (
                            <ChevronRight className="ml-auto" size={14} />
                          )}
                        </button>

                        {/* Nested submenu */}
                        {dropItem.dropdown && activeSubmenu === dropItem.label && (
                          <div className="absolute left-full top-0 pl-2">
                            <div className="min-w-[200px] rounded-lg border border-neutral-200 bg-white py-2 shadow-lg">
                              {dropItem.dropdown.map((subItem) => (
                                <button
                                  key={subItem.label}
                                  onClick={() => handleDropdownClick(subItem)}
                                  className="landing-nav-dropdown-item w-full"
                                >
                                  <span>{subItem.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          </nav>
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:flex shrink-0 items-center gap-5">
          <a
            href="https://freshtails.zopnote.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-neutral-900 transition-colors hover:text-primary"
          >
            Login
          </a>
          <Button
            className="h-9 rounded-md bg-primary px-5 text-sm font-semibold text-white shadow-none hover:bg-primary/90 hover:shadow-none focus-visible:shadow-none active:shadow-none"
            asChild
          >
            <a href="https://freshtails.zopnote.com/signup" target="_blank" rel="noopener noreferrer">Sign Up</a>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="absolute right-2 p-2 text-neutral-900 hover:text-black sm:right-4 lg:hidden"
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Info bar — always visible */}
      <div className="min-h-14 border-t border-primary/15 bg-primary-light transition-all duration-300 sm:min-h-16">
        <div className={`mx-auto flex max-w-[1480px] items-center justify-between gap-2 px-4 py-2.5 transition-all duration-300 sm:gap-3 sm:px-6 sm:py-3 lg:px-8 ${isScrolled ? 'sm:py-1.5' : ''}`}>
          <p className={`hidden font-medium text-neutral-800 transition-all duration-300 sm:block ${isScrolled ? 'text-sm' : 'text-base'}`}>
            Bangalore's most trusted home for happy pets every day of the week
          </p>
          <p className="min-w-0 flex-1 truncate text-xs font-medium text-neutral-800 sm:hidden">
            Trusted home for happy pets
          </p>
          <div className="flex flex-none items-center justify-end gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setBookingOpen(true)}
              className={`inline-flex h-9 flex-none items-center justify-center gap-1.5 rounded-full bg-neutral-900 px-4 text-xs font-bold text-white transition-all duration-300 hover:bg-neutral-800 sm:gap-2 sm:text-xs ${isScrolled ? 'sm:h-8 sm:px-5' : 'sm:h-10 sm:px-7'}`}
            >
              <CalendarCheck size={16} strokeWidth={2.25} />
              Book Now
            </button>
            <a
              href="https://wa.me/918861712345"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp Now"
              className={`inline-flex h-9 w-9 flex-none items-center justify-center gap-1.5 rounded-full border border-[#25D366]/30 bg-white text-xs font-bold text-[#25D366] transition-all duration-300 hover:bg-[#25D366] hover:text-white sm:w-auto sm:gap-2 sm:text-xs ${isScrolled ? 'sm:h-8 sm:px-5' : 'sm:h-10 sm:px-7'}`}
            >
              <span
                aria-hidden="true"
                className="h-4 w-4 bg-current"
                style={{
                  WebkitMaskImage: 'url(/images/whatsapp.svg)',
                  maskImage: 'url(/images/whatsapp.svg)',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                }}
              />
              <span className="hidden sm:inline">WhatsApp Now</span>
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-neutral-200 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="space-y-4 px-4 py-6 text-center">
            {navItems.map((item) => (
              <div key={item.label}>
                <a
                  href={item.href || item.path || '#'}
                  className="landing-nav-mobile-primary"
                  onClick={(e) => {
                    if (item.dropdown) {
                      e.preventDefault();
                      setActiveDropdown(activeDropdown === item.label ? null : item.label);
                    } else {
                      handleNavClick(item, e);
                      setIsMobileMenuOpen(false);
                    }
                  }}
                >
                  {item.label}
                </a>
                {item.dropdown && activeDropdown === item.label && (
                  <div className="ml-4 mt-2 space-y-2">
                    {item.dropdown.map((dropItem) => (
                      <div key={dropItem.label}>
                        <button
                          onClick={() => {
                            if (dropItem.dropdown) {
                              setMobileSubmenu(mobileSubmenu === dropItem.label ? null : dropItem.label);
                            } else {
                              handleDropdownClick(dropItem);
                              setIsMobileMenuOpen(false);
                            }
                          }}
                          className="landing-nav-mobile-secondary flex w-full items-center justify-between"
                        >
                          <span>{dropItem.label}</span>
                          {dropItem.dropdown && <ChevronRight size={14} />}
                          {dropItem.isNew && (
                            <span className="rounded-full bg-gradient-to-r from-primary to-primary-dark px-2 py-0.5 text-xs font-semibold text-white">
                              New
                            </span>
                          )}
                        </button>
                        {dropItem.dropdown && mobileSubmenu === dropItem.label && (
                          <div className="ml-4 mt-1 space-y-1">
                            {dropItem.dropdown.map((subItem) => (
                              <button
                                key={subItem.label}
                                onClick={() => {
                                  handleDropdownClick(subItem);
                                  setIsMobileMenuOpen(false);
                                }}
                                className="landing-nav-mobile-secondary text-neutral-600"
                              >
                                {subItem.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 space-y-3">
              <Button variant="outline" className="w-full text-sm font-semibold" asChild>
                <a href="https://freshtails.zopnote.com/login" target="_blank" rel="noopener noreferrer">Login</a>
              </Button>
              <Button className="w-full bg-primary text-sm font-semibold shadow-none hover:bg-primary/90 hover:shadow-none" asChild>
                <a href="https://freshtails.zopnote.com/signup" target="_blank" rel="noopener noreferrer">Sign Up</a>
              </Button>
            </div>
          </div>
        </div>
      )}
      <BookingEnquiryDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </header>
  );
};

export default Header;
